import { STREAMING_CHUNK_DELIMITER } from '@libs/constants';

/** The newline that terminates an SSE comment line. */
const LF = '\n';

/**
 * Drop SSE comment lines from the head of the buffer.
 *
 * A comment (`: ok`) is framed by a newline, not by
 * `STREAMING_CHUNK_DELIMITER` — the API writes one before the first event to
 * flush the response headers (see `pipeChatStream`). Because it carries no
 * delimiter it would otherwise sit in the buffer and get glued onto the front of
 * the next event, and a caller that skips anything starting with `:` would then
 * discard that event along with the comment. That cost the new-chat flow its
 * `workflow_started` event, which is the only place the conversation id to
 * navigate to comes from — so the first message in a new chat did nothing.
 *
 * Returns null when a comment has started but its terminating newline has not
 * arrived yet, meaning the caller should keep buffering and emit nothing.
 */
const stripLeadingComments = (buffer: string): string | null => {
  let rest = buffer;

  // Several comments can arrive back to back (the prelude plus any keepalive).
  for (;;) {
    // Only whitespace may precede a comment — typically the blank line left by
    // the previous comment's terminator.
    const start = rest.search(/\S/);
    // Nothing but the whitespace a comment terminator left behind.
    if (start === -1) return '';
    // Real content: hand it back without the leading blank line the comment's
    // `\n\n` terminator leaves, so an event arrives byte-exact.
    if (rest[start] !== ':') return rest.slice(start);

    const newline = rest.indexOf(LF, start);
    if (newline === -1) return null; // comment still arriving
    rest = rest.slice(newline + 1);
  }
};

/**
 * Split a read buffer into the complete delimited events it holds, returning
 * whatever trailing fragment is not yet a whole event.
 *
 * The stream is a sequence of JSON events separated by
 * `STREAMING_CHUNK_DELIMITER`, but a network read ends wherever the transport
 * decides — regularly in the middle of an event. The trailing fragment must
 * therefore be carried into the next read instead of being parsed on its own,
 * which is what silently dropped slices of streamed answers (a lost markdown
 * table row renders the whole table as plain text).
 *
 * Lives apart from `streaming.ts` so it can be tested without importing that
 * module's config/DI graph, which validates env vars at load time.
 */
export const takeCompleteEvents = (
  buffer: string,
): { events: string[]; rest: string } => {
  const withoutComments = stripLeadingComments(buffer);

  // A comment whose end has not arrived: hold everything and wait, rather than
  // split a half-arrived comment into a bogus event.
  if (withoutComments === null) return { events: [], rest: buffer };

  const parts = withoutComments.split(STREAMING_CHUNK_DELIMITER);
  // `pop()` always removes the segment after the last delimiter. That segment is
  // a complete event only if the buffer happened to end exactly on a delimiter,
  // in which case it is the empty string and carrying it costs nothing.
  const rest = parts.pop() ?? '';
  return { events: parts, rest };
};
